import { MouseEvent } from 'react'
import { flushSync } from 'react-dom'
import { To } from 'react-router-dom'

/**
 * Runs `updateDom` inside the native View Transitions API when supported, cross-fading
 * between the old and new DOM (see `view-transition-name: page-content` in AppWrapper.scss).
 *
 * react-router-dom's own View Transition integration (`unstable_viewTransition`) only takes
 * effect with a data router (`createBrowserRouter` + `RouterProvider`); this app still uses
 * the classic `<BrowserRouter>`, so navigation-triggered transitions have to be driven
 * manually here. `flushSync` forces the DOM update to commit synchronously inside the
 * transition callback, which the View Transitions API requires in order to capture "before"
 * and "after" snapshots. Falls back to a plain synchronous update in unsupported browsers.
 */
export const withViewTransition = (updateDom: () => void): void => {
  if (typeof document === 'undefined' || typeof document.startViewTransition !== 'function') {
    updateDom()
    return
  }
  document.startViewTransition(() => flushSync(updateDom))
}

/**
 * Builds an anchor `onClick` handler that navigates via `navigate` (instead of the anchor's
 * default full navigation) so the transition above applies to `NavLink`/`Link` clicks too.
 * Not a hook itself so it can be created per-item inside a list without violating the rules
 * of hooks; pass in a `navigate` function obtained from `useViewTransitionNavigate`.
 * Modified clicks (new tab, etc.) fall through to the default anchor behaviour.
 */
export const createViewTransitionLinkClickHandler = (navigate: (to: To) => void, to: To) => (event: MouseEvent<HTMLAnchorElement>) => {
  const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  if (event.defaultPrevented || event.button !== 0 || isModifiedClick) {
    return
  }
  event.preventDefault()
  navigate(to)
}
